import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Store, Tag, Folder, MapPin, ArrowLeft, Inbox, KeyRound, UploadCloud, ClipboardList, Settings, Handshake, Sparkles, Megaphone, Building2, Star, Heart, CalendarDays, Bell, LifeBuoy, Flag, AlertTriangle, Newspaper, BarChart3, TrendingUp, Crown, Menu, X, Facebook, Trophy, ChevronDown, Layout, Rss, HandHeart, Mail, FileText, IdCard } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import GlobalSearch from "@/components/admin/GlobalSearch";

const TODO_LABELS = {
  requests: ["demande(s)", "/admin/demandes"],
  signups: ["inscription(s) partenaire", "/admin/partenaires-inscriptions"],
  partner_fiches: ["fiche(s) à publier/vérifier", "/admin/fiches-partenaires"],
  pros: ["nouveau(x) partenaire(s)", "/admin/professionnels"],
  offers: ["offre(s) à modérer", "/admin/offres"],
  reviews: ["avis à modérer", "/admin/avis"],
  reports: ["signalement(s)", "/admin/signalements"],
  support: ["ticket(s) support", "/admin/support"],
  orgs: ["organisme(s) à valider", "/admin/organismes"],
  associations: ["association(s) à valider", "/admin/associations"],
  clubs: ["club(s) à valider", "/admin/espace-clubs"],
  recos: ["recommandation(s)", "/admin/recommandations"],
  fbposts: ["publication(s) Facebook", "/admin/publications-facebook"],
  reported_comments: ["commentaire(s) signalé(s)", "/admin/revue-presse"],
  neighbor_reports: ["signalement(s) Entre Voisins", "/admin/entre-voisins"],
  events_pending: ["événement(s) à valider", "/admin/evenements"],
};

// Navigation regroupée en 6 groupes repliables (plusieurs ouvrables en même temps).
const GROUPS = [
  {
    id: "overview", title: "Vue d'ensemble", collapsible: false, defaultOpen: true,
    items: [
      { to: "/admin", exact: true, label: "Dashboard", icon: LayoutDashboard, testId: "admin-nav-dashboard" },
    ],
  },
  {
    id: "todos", title: "À traiter", defaultOpen: true,
    items: [
      { to: "/admin/demandes", label: "Demandes", icon: Inbox, badgeKey: "requests", testId: "admin-nav-requests" },
      { to: "/admin/partenaires-inscriptions", label: "Inscriptions", icon: Handshake, badgeKey: "signups", testId: "admin-nav-signups" },
      { to: "/admin/relances-fiche", label: "Relances fiche", icon: Inbox, testId: "admin-nav-fiche-reminders" },
      { to: "/admin/relances-clubs-assos", label: "Relances clubs/assos", icon: Inbox, testId: "admin-nav-entity-reminders" },
      { to: "/admin/avis", label: "Avis à modérer", icon: Star, badgeKey: "reviews", testId: "admin-nav-reviews" },
      { to: "/admin/signalements", label: "Signalements", icon: Flag, badgeKey: "reports", testId: "admin-nav-reports" },
      { to: "/admin/support", label: "Support client", icon: LifeBuoy, badgeKey: "support", testId: "admin-nav-support" },
      { to: "/admin/recommandations", label: "Recommandations", icon: Heart, badgeKey: "recos", testId: "admin-nav-recos" },
    ],
  },
  {
    id: "partners", title: "Partenaires & Offres", defaultOpen: false,
    items: [
      { to: "/admin/professionnels", label: "Partenaires", icon: Store, badgeKey: "pros", testId: "admin-nav-pros" },
      { to: "/admin/fiches-partenaires", label: "Fiches partenaires", icon: ClipboardList, badgeKey: "partner_fiches", testId: "admin-nav-partner-fiches" },
      { to: "/admin/offres", label: "Offres & Remises", icon: Tag, badgeKey: "offers", testId: "admin-nav-offers" },
      { to: "/admin/formules-partenaires", label: "Formules & Tarifs", icon: Sparkles, testId: "admin-nav-partner-config" },
      { to: "/admin/organismes", label: "Organismes & CSE", icon: Building2, badgeKey: "orgs", testId: "admin-nav-orgs" },
      { to: "/admin/associations", label: "Associations", icon: Heart, badgeKey: "associations", testId: "admin-nav-associations" },
      { to: "/admin/espace-clubs", label: "Clubs sportifs", icon: Trophy, badgeKey: "clubs", testId: "admin-nav-espace-clubs" },
      { to: "/admin/campagne-fondateur", label: "Campagne Fondateur", icon: Crown, testId: "admin-nav-founders" },
    ],
  },
  {
    id: "ai-social", title: "IA & Réseaux sociaux", defaultOpen: false,
    items: [
      { to: "/admin/studio-ia", label: "Studio IA", icon: Sparkles, testId: "admin-nav-ai-studio" },
      { to: "/admin/reseaux-sociaux", exact: true, label: "Studio du jour", icon: Megaphone, testId: "admin-nav-social" },
      { to: "/admin/groupes-facebook", label: "Groupes Facebook", icon: Users, testId: "admin-nav-social-groups" },
      { to: "/admin/publications-facebook", label: "Publications des pros", icon: Facebook, badgeKey: "fbposts", late: true, testId: "admin-nav-social-proposts" },
      { to: "/admin/publications-offres", label: "Publications des offres", icon: Tag, testId: "admin-nav-social-offerposts" },
      { to: "/admin/publications-evenements", label: "Publications événements", icon: CalendarDays, testId: "admin-nav-social-eventposts" },
      { to: "/admin/portee-reseaux", label: "Portée réseaux", icon: BarChart3, testId: "admin-nav-social-reach" },
    ],
  },
  {
    id: "members-content", title: "Membres & Contenu", defaultOpen: false,
    items: [
      { to: "/admin/utilisateurs", label: "Utilisateurs", icon: Users, badgeKey: "new_users", testId: "admin-nav-users" },
      { to: "/admin/clubs", label: "Clubs & CSE", icon: Users, testId: "admin-nav-clubs" },
      { to: "/admin/codes", label: "Codes d'accès", icon: KeyRound, testId: "admin-nav-codes" },
      { to: "/admin/evenements", label: "Événements locaux", icon: CalendarDays, badgeKey: "events_pending", testId: "admin-nav-events" },
      { to: "/admin/petites-annonces", label: "Petites annonces", icon: Megaphone, testId: "admin-nav-annonces" },
      { to: "/admin/blog", label: "Blog local", icon: Newspaper, testId: "admin-nav-blog" },
      { to: "/admin/revue-presse", label: "Revue de presse", icon: Rss, badgeKey: "reported_comments", testId: "admin-nav-press" },
      { to: "/admin/revue-presse-3-art", label: "Revue de presse 3 art", icon: Rss, testId: "admin-nav-press-3art" },
      { to: "/admin/revues-de-presse", label: "Revues quotidiennes", icon: Newspaper, testId: "admin-nav-revues" },
      { to: "/admin/entre-voisins", label: "Entre Voisins", icon: HandHeart, badgeKey: "neighbor_reports", testId: "admin-nav-voisins" },
      { to: "/admin/devis", label: "Demandes de devis", icon: FileText, testId: "admin-nav-quotes" },
      { to: "/admin/editeur-site", label: "Éditeur de site", icon: Layout, testId: "admin-nav-site-builder" },
      { to: "/admin/notifications", label: "Notifications push", icon: Bell, testId: "admin-nav-push" },
      { to: "/admin/cartes", label: "Cartes digitales", icon: IdCard, testId: "admin-nav-cartes" },
    ],
  },
  {
    id: "analytics-config", title: "Analyse & Config", defaultOpen: false,
    items: [
      { to: "/admin/statistiques-partage", label: "Statistiques", icon: BarChart3, testId: "admin-nav-share-stats" },
      { to: "/admin/audience", label: "Analyse d'audience", icon: TrendingUp, testId: "admin-nav-audience" },
      { to: "/admin/couts-ia", label: "Suivi des coûts IA", icon: Sparkles, testId: "admin-nav-ai-costs" },
      { to: "/admin/journal", label: "Journal d'activité", icon: ClipboardList, testId: "admin-nav-journal" },
      { to: "/admin/emails", label: "E-mails & Communications", icon: Mail, testId: "admin-nav-emails" },
      { to: "/admin/journal-emails", label: "E-mails non délivrés", icon: Mail, testId: "admin-nav-email-logs" },
      { to: "/admin/categories", label: "Catégories", icon: Folder, testId: "admin-nav-cats" },
      { to: "/admin/villes", label: "Villes & Territoires", icon: MapPin, testId: "admin-nav-cities" },
      { to: "/admin/import", label: "Import CSV", icon: UploadCloud, testId: "admin-nav-import" },
      { to: "/admin/parametres", label: "Paramètres système", icon: Settings, testId: "admin-nav-settings" },
    ],
  },
];

export default function AdminLayout() {
  const [todos, setTodos] = useState({ total: 0, fb_late: 0, sections: {} });
  const [open, setOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => {
    const init = {};
    GROUPS.forEach((g) => { init[g.id] = g.defaultOpen; });
    return init;
  });
  const lastReq = useRef(null);
  const location = useLocation();

  // Ouvre automatiquement le groupe qui contient la route active (accès direct / favori / lien bannière).
  useEffect(() => {
    const path = location.pathname;
    const grp = GROUPS.find((g) => g.collapsible !== false && g.items.some((it) => (it.exact ? path === it.to : (path === it.to || path.startsWith(it.to + "/")))));
    if (grp) setOpenGroups((g) => (g[grp.id] ? g : { ...g, [grp.id]: true }));
  }, [location.pathname]);

  const fetchTodos = async () => {
    try {
      const { data } = await api.get("/admin/todos");
      const req = data?.sections?.requests || 0;
      if (lastReq.current !== null && req > lastReq.current) {
        toast.info(`${req - lastReq.current} nouvelle(s) demande(s) reçue(s) !`, { duration: 6000 });
      }
      lastReq.current = req;
      setTodos(data || { total: 0, fb_late: 0, sections: {} });
    } catch (e) { void e; }
  };

  useEffect(() => {
    fetchTodos();
    const iv = setInterval(fetchTodos, 20000);
    const onRefresh = () => fetchTodos();
    window.addEventListener("requests:refresh", onRefresh);
    window.addEventListener("admin:todos-refresh", onRefresh);
    return () => { clearInterval(iv); window.removeEventListener("requests:refresh", onRefresh); window.removeEventListener("admin:todos-refresh", onRefresh); };
  }, []);

  // Consulter l'onglet Utilisateurs efface la pastille "nouvelles inscriptions".
  useEffect(() => {
    if (location.pathname.startsWith("/admin/utilisateurs")) {
      api.post("/admin/users/mark-seen").then(fetchTodos).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const s = todos.sections || {};
  const close = () => setOpen(false);
  const toggleGroup = (id) => setOpenGroups((g) => ({ ...g, [id]: !g[id] }));
  const groupTotal = (g) => g.items.reduce((sum, it) => sum + (it.badgeKey ? (s[it.badgeKey] || 0) : 0), 0);

  const link = ({ isActive }) =>
    `flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
      isActive ? "bg-slate-800 text-white font-semibold border-l-2 border-[#E05638] pl-2.5" : "text-slate-400 hover:bg-slate-800/70 hover:text-slate-200"
    }`;

  const Badge = ({ k, late }) => {
    const n = s[k] || 0;
    if (!n) return null;
    return (
      <span data-testid={`admin-nav-badge-${k}`}
            className={`ml-auto min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full text-white text-[11px] font-extrabold shadow-sm ${late ? "bg-rose-600 animate-pulse" : "bg-rose-500"}`}>
        {n}
      </span>
    );
  };

  const NavItems = () => (
    <nav className="space-y-1" onClick={close}>
      {GROUPS.map((g) => {
        if (g.collapsible === false) {
          return (
            <div key={g.id} className="space-y-0.5">
              {g.items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.exact} className={link} data-testid={it.testId}>
                  <span className="inline-flex items-center gap-2"><it.icon size={16} /> {it.label}</span>
                  {it.badgeKey && <Badge k={it.badgeKey} late={it.late && todos.fb_late > 0} />}
                </NavLink>
              ))}
            </div>
          );
        }
        const isOpen = openGroups[g.id];
        const total = groupTotal(g);
        return (
          <div key={g.id} className="pt-1">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleGroup(g.id); }}
              data-testid={`admin-group-${g.id}`}
              className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-slate-300 transition-colors rounded-lg hover:bg-slate-800/50"
            >
              <span>{g.title}</span>
              <span className="inline-flex items-center gap-1.5">
                {!isOpen && total > 0 && (
                  <span data-testid={`admin-group-badge-${g.id}`} className="min-w-[18px] h-4 px-1 grid place-items-center rounded-full bg-rose-500 text-white text-[10px] font-bold">{total}</span>
                )}
                <ChevronDown size={14} className={`transition-transform ${isOpen ? "" : "-rotate-90"}`} />
              </span>
            </button>
            {isOpen && (
              <div className="mt-0.5 space-y-0.5">
                {g.items.map((it) => (
                  <NavLink key={it.to} to={it.to} end={it.exact} className={link} data-testid={it.testId}>
                    <span className="inline-flex items-center gap-2"><it.icon size={16} /> {it.label}</span>
                    {it.badgeKey && <Badge k={it.badgeKey} late={it.late && todos.fb_late > 0} />}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen md:flex bg-slate-900">
      {/* Barre supérieure mobile */}
      <div className="md:hidden sticky top-0 z-40 flex items-center gap-3 bg-slate-900 text-white px-4 py-3 border-b border-slate-800">
        <button onClick={() => setOpen(true)} data-testid="admin-mobile-menu-toggle" className="p-1.5 -ml-1.5 rounded-lg hover:bg-slate-800" aria-label="Ouvrir le menu">
          <Menu size={22}/>
        </button>
        <span className="font-heading font-extrabold text-lg">Back-office</span>
        {todos.total > 0 && (
          <span className="ml-auto inline-flex items-center gap-1 px-2 h-6 rounded-full bg-rose-500 text-white text-xs font-bold">
            <AlertTriangle size={12}/> {todos.total}
          </span>
        )}
      </div>

      {/* Sidebar desktop */}
      <aside className="w-64 bg-slate-900 text-slate-100 p-4 hidden md:flex flex-col border-r border-slate-800 shrink-0 select-none overflow-y-auto">
        <Link to="/" className="text-xs text-slate-400 inline-flex items-center gap-1 mb-4 hover:text-slate-200 transition-colors"><ArrowLeft size={14}/> Retour au site</Link>
        <div className="flex items-center gap-2 mb-3">
          <div className="font-heading font-extrabold text-xl">Back-office</div>
          {todos.total > 0 && (
            <span data-testid="admin-todos-total" title={`${todos.total} action(s) en attente`}
                  className="inline-flex items-center gap-1 px-2.5 h-6 rounded-full bg-rose-500 text-white text-xs font-bold shadow-sm">
              <AlertTriangle size={12}/> {todos.total}
            </span>
          )}
        </div>
        <div className="mb-5"><GlobalSearch/></div>
        <NavItems/>
      </aside>

      {/* Drawer mobile */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close} />
          <aside className="absolute left-0 top-0 h-full w-72 max-w-[85%] bg-slate-900 text-slate-100 p-4 flex flex-col overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <Link to="/" className="text-xs text-slate-400 inline-flex items-center gap-1"><ArrowLeft size={14}/> Retour au site</Link>
              <button onClick={close} data-testid="admin-mobile-menu-close" className="p-1.5 rounded-lg hover:bg-slate-800" aria-label="Fermer le menu"><X size={20}/></button>
            </div>
            <div className="font-heading font-extrabold text-xl mb-3">Back-office</div>
            <div className="mb-5"><GlobalSearch/></div>
            <NavItems/>
          </aside>
        </div>
      )}

      <main className="flex-1 bg-slate-50 p-4 sm:p-6 md:p-10 overflow-x-auto min-w-0">
        {todos.total > 0 && (
          <div data-testid="admin-todo-banner" className="mb-5 rounded-2xl border border-amber-300 bg-amber-50/90 p-3 sm:p-4 shadow-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={18}/>
              <div className="min-w-0">
                <div className="font-bold text-amber-900 text-sm">
                  {todos.total} opération(s) en attente de votre validation
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(TODO_LABELS).map(([k, [label, to]]) => (
                    (s[k] || 0) > 0 ? (
                      <Link key={k} to={to} onClick={close} data-testid={`admin-todo-link-${k}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-amber-300 text-amber-800 text-xs font-semibold hover:bg-amber-100">
                        <span className="min-w-[18px] h-4 px-1 grid place-items-center rounded-full bg-rose-500 text-white text-[10px] font-bold">{s[k]}</span>
                        {label}
                      </Link>
                    ) : null
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        <Outlet/>
      </main>
    </div>
  );
}
